import { Injectable, computed, isDevMode, signal } from '@angular/core';
import { CallStatus } from '../models/call-status.enum';
import { CallSession } from '../models/call-session.model';
import { DeviceState } from '../models/device-state.model';
import {
  TelephonyState,
  INITIAL_TELEPHONY_STATE,
} from '../models/telephony-state.model';

/**
 * Valid state transitions for the call state machine.
 * Invalid transitions are silently ignored (warning logged in dev mode).
 */
const VALID_TRANSITIONS: ReadonlyMap<CallStatus, readonly CallStatus[]> =
  new Map([
    [
      CallStatus.Idle,
      [CallStatus.Registering, CallStatus.Reconnecting],
    ],
    [
      CallStatus.Registering,
      [
        CallStatus.Registered,
        CallStatus.Failed,
        CallStatus.Idle,
        CallStatus.Reconnecting,
      ],
    ],
    [
      CallStatus.Registered,
      [
        CallStatus.Registering,
        CallStatus.Trying,
        CallStatus.Ringing,
        CallStatus.Idle,
        CallStatus.Reconnecting,
      ],
    ],
    [
      CallStatus.Trying,
      [
        CallStatus.Ringing,
        CallStatus.Active,
        CallStatus.Failed,
        CallStatus.Terminated,
        CallStatus.HangingUp,
        CallStatus.Reconnecting,
      ],
    ],
    [
      CallStatus.Ringing,
      [
        CallStatus.Active,
        CallStatus.Failed,
        CallStatus.Terminated,
        CallStatus.HangingUp,
        CallStatus.Reconnecting,
      ],
    ],
    [
      CallStatus.Active,
      [
        CallStatus.Held,
        CallStatus.HangingUp,
        CallStatus.Terminated,
        CallStatus.Reconnecting,
      ],
    ],
    [
      CallStatus.Held,
      [
        CallStatus.Active,
        CallStatus.HangingUp,
        CallStatus.Terminated,
        CallStatus.Reconnecting,
      ],
    ],
    [
      CallStatus.HangingUp,
      [
        CallStatus.Terminated,
        CallStatus.Failed,
        CallStatus.Reconnecting,
      ],
    ],
    [
      CallStatus.Terminated,
      [
        CallStatus.Registered,
        CallStatus.Idle,
        CallStatus.Reconnecting,
      ],
    ],
    [
      CallStatus.Failed,
      [CallStatus.Idle, CallStatus.Registering, CallStatus.Reconnecting],
    ],
    [
      CallStatus.Reconnecting,
      [CallStatus.Registering, CallStatus.Failed, CallStatus.Idle],
    ],
  ]);

/**
 * Central signal-based store for telephony call state.
 * All state mutations happen exclusively through this store.
 * Components must NOT import this directly — use CallFacadeService instead.
 */
@Injectable({ providedIn: 'root' })
export class CallStateStore {
  private readonly state = signal<TelephonyState>(INITIAL_TELEPHONY_STATE);

  /** Current call status */
  readonly callStatus = computed(() => this.state().callStatus);

  /** Current active call session */
  readonly currentSession = computed(() => this.state().currentSession);

  /** Audio device state */
  readonly deviceState = computed(() => this.state().deviceState);

  /** Last error message */
  readonly lastError = computed(() => this.state().lastError);

  /** Number of reconnection attempts */
  readonly reconnectAttempts = computed(() => this.state().reconnectAttempts);

  /** Whether a call is currently in progress */
  readonly isCallActive = computed(() => {
    const status = this.state().callStatus;
    return (
      status === CallStatus.Trying ||
      status === CallStatus.Ringing ||
      status === CallStatus.Active ||
      status === CallStatus.Held
    );
  });

  /** Whether the agent is registered and ready to make/receive calls */
  readonly isRegistered = computed(
    () => this.state().callStatus === CallStatus.Registered
  );

  /** Whether the current call is muted (false when no session) */
  readonly isMuted = computed(
    () => this.state().currentSession?.isMuted ?? false
  );

  /** Whether the current call is on hold (false when no session) */
  readonly isHeld = computed(
    () => this.state().currentSession?.isHeld ?? false
  );

  /** Full snapshot of current state (readonly) */
  readonly snapshot = computed(() => this.state());

  /**
   * Transition the call status to a new state.
   * Invalid transitions are silently ignored with a dev-mode warning.
   */
  transition(next: CallStatus): boolean {
    const current = this.state().callStatus;
    const allowed = VALID_TRANSITIONS.get(current);

    if (!allowed || !allowed.includes(next)) {
      if (isDevMode()) {
        console.warn(
          `[CallStateStore] Invalid transition: ` +
            `${CallStatus[current]} → ${CallStatus[next]}`
        );
      }
      return false;
    }

    this.patch({ callStatus: next });

    if (next === CallStatus.Idle) {
      this.patch({
        currentSession: null,
        lastError: null,
        reconnectAttempts: 0,
      });
    }

    return true;
  }

  /** Update the current call session */
  setSession(session: CallSession | null): void {
    this.patch({ currentSession: session });
  }

  /** Update mute state on the current session */
  setMuted(muted: boolean): void {
    const session = this.state().currentSession;
    if (session) {
      this.patch({ currentSession: { ...session, isMuted: muted } });
    }
  }

  /** Update hold state on the current session */
  setHeld(held: boolean): void {
    const session = this.state().currentSession;
    if (session) {
      this.patch({ currentSession: { ...session, isHeld: held } });
    }
  }

  /**
   * Marks whether the backend stamps the current session's agent-leg SIP Call-ID. Ignored when the
   * session was replaced or cleared since `sessionId` was captured, so a late response header can
   * never land on a different call.
   */
  setBackendStampsAgentLegCallId(sessionId: string, value: boolean): void {
    const session = this.state().currentSession;
    if (session && session.id === sessionId) {
      this.patch({
        currentSession: { ...session, backendStampsAgentLegCallId: value },
      });
    }
  }

  /** Update audio device state */
  setDeviceState(deviceState: Partial<DeviceState>): void {
    this.patch({
      deviceState: { ...this.state().deviceState, ...deviceState },
    });
  }

  /** Set last error */
  setError(error: string | null): void {
    this.patch({ lastError: error });
  }

  /** Increment the reconnection attempt counter */
  incrementReconnectAttempts(): void {
    this.patch({
      reconnectAttempts: this.state().reconnectAttempts + 1,
    });
  }

  /** Reset reconnect attempts to zero after successful recovery */
  resetReconnectAttempts(): void {
    this.patch({ reconnectAttempts: 0 });
  }

  /** Reset the store to initial state */
  reset(): void {
    this.state.set(INITIAL_TELEPHONY_STATE);
  }

  /** Apply a partial state update */
  private patch(partial: Partial<TelephonyState>): void {
    this.state.update(current => ({ ...current, ...partial }));
  }
}
